/** Per-campaign notification template editor — Phase 2E-tmpl-camp.
 *
 * Sits beside the org-level template surface (Admin > Branding). Each
 * (kind, channel) slot has three possible states:
 *
 *   1. "Uses default"      — no org row, no campaign row
 *   2. "Inherits org override" — org row exists, no campaign row
 *   3. "Custom for this campaign" — campaign row exists
 *
 * The textarea always seeds with the wording that would actually ship
 * if the operator sent right now (campaign → org → default), so the
 * "Save" button is a clean "promote this view into a campaign-specific
 * row" affordance. "Reset" only appears when the campaign row exists +
 * deletes it so the slot falls back to inheriting the org default. */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { Trans, useTranslation } from "react-i18next";

import { TemplatePreviewAndTest } from "@/components/TemplatePreviewAndTest";
import { ApiPaths, api } from "@/lib/api";


type Kind =
  | "invite"
  | "reminder_24h"
  | "reminder_1h"
  | "confirmation"
  | "rescheduled"
  | "booking_invite";
type Channel = "email" | "sms";

type Defaults = {
  variables: Record<string, string>;
  defaults: { kind: Kind; channel: Channel; subject: string; body_text: string }[];
};

type CustomRow = {
  id: string;
  campaign: string | null;
  kind: Kind;
  channel: Channel;
  subject: string;
  body_text: string;
  enabled: boolean;
  updated_at: string;
};


const KIND_KEYS: Record<Kind, string> = {
  invite: "admin.notifications.kindInvite",
  reminder_24h: "admin.notifications.kindReminder24h",
  reminder_1h: "admin.notifications.kindReminder1h",
  confirmation: "admin.notifications.kindConfirmation",
  rescheduled: "admin.notifications.kindRescheduled",
  booking_invite: "admin.notifications.kindBookingInvite",
};

const CHANNEL_KEYS: Record<Channel, string> = {
  email: "admin.notifications.channelEmail",
  sms: "admin.notifications.channelSms",
};


type Props = { campaignId: string };

export function CampaignNotificationTemplatesCard({ campaignId }: Props) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [kind, setKind] = useState<Kind>("invite");
  const [channel, setChannel] = useState<Channel>("email");

  // Preview the baked-in defaults in the language messages actually send
  // in — the send-time fallback is French (no per-org locale; recipient
  // Session.locale / Contact.language drive real sends, blank → French).
  // Previewing in the admin's UI language (e.g. English) was misleading:
  // a French org's beneficiaries never receive those English defaults.
  const previewLocale = "fr";
  const defaultsQ = useQuery({
    // Campaign-scoped: `campaign` layers this campaign's {cf_*} custom-field
    // variables onto the built-in list the editor advertises.
    queryKey: ["notification-template-defaults", previewLocale, campaignId],
    queryFn: async () =>
      (
        await api.get<Defaults>(ApiPaths.notificationTemplateDefaults, {
          params: { locale: previewLocale, campaign: campaignId },
        })
      ).data,
  });
  // Org-level rows (so the "Inherits org override" state can show the
  // actual org wording in the textarea as the seed).
  const orgRowsQ = useQuery({
    queryKey: ["notification-templates"],
    queryFn: async () =>
      (await api.get<CustomRow[]>(ApiPaths.notificationTemplates)).data,
  });
  const campRowsQ = useQuery({
    queryKey: ["campaign-notification-templates", campaignId],
    queryFn: async () =>
      (
        await api.get<CustomRow[]>(
          ApiPaths.campaignNotificationTemplates(campaignId),
        )
      ).data,
  });

  const [subject, setSubject] = useState("");
  const [bodyText, setBodyText] = useState("");
  const [touched, setTouched] = useState(false);

  // Defensive normalisation: a malformed (non-array) response for any of
  // these list endpoints must not crash the card into a remount loop —
  // `.find`/`.some` below would throw on a non-array. Memoised so the
  // refs stay stable across renders (they feed the effect deps).
  const campRows = useMemo(
    () => (Array.isArray(campRowsQ.data) ? campRowsQ.data : []),
    [campRowsQ.data],
  );
  const orgRows = useMemo(
    () => (Array.isArray(orgRowsQ.data) ? orgRowsQ.data : []),
    [orgRowsQ.data],
  );
  const defaultRows = useMemo(
    () => (Array.isArray(defaultsQ.data?.defaults) ? defaultsQ.data.defaults : []),
    [defaultsQ.data],
  );

  // Seed the editor: campaign row beats org row beats baked-in
  // default, matching what `_resolve_template` does at send time.
  useEffect(() => {
    if (!defaultsQ.data) return;
    const camp = campRows.find(
      (r) => r.kind === kind && r.channel === channel,
    );
    const org = orgRows.find(
      (r) => r.kind === kind && r.channel === channel,
    );
    const def = defaultRows.find(
      (d) => d.kind === kind && d.channel === channel,
    );
    const fallbackSubject = org?.subject || def?.subject || "";
    const fallbackBody = org?.body_text || def?.body_text || "";
    setSubject(camp?.subject || fallbackSubject);
    setBodyText(camp?.body_text || fallbackBody);
    setTouched(false);
  }, [kind, channel, defaultsQ.data, campRows, orgRows, defaultRows]);

  const save = useMutation({
    mutationFn: async () => {
      const res = await api.put<CustomRow>(
        ApiPaths.campaignNotificationTemplate(campaignId, kind, channel),
        { subject, body_text: bodyText },
      );
      return res.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: ["campaign-notification-templates", campaignId],
      });
      setTouched(false);
    },
  });

  const reset = useMutation({
    mutationFn: async () => {
      await api.delete(
        ApiPaths.campaignNotificationTemplate(campaignId, kind, channel),
      );
    },
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: ["campaign-notification-templates", campaignId],
      });
    },
  });

  if (defaultsQ.isLoading || campRowsQ.isLoading || orgRowsQ.isLoading) {
    return (
      <section className="card text-sm text-nb-text-2">
        {t("admin.notifications.loading")}
      </section>
    );
  }
  if (!defaultsQ.data) return null;

  const variables = defaultsQ.data.variables;
  const hasCampaignRow = campRows.some(
    (r) => r.kind === kind && r.channel === channel,
  );
  const hasOrgRow = orgRows.some(
    (r) => r.kind === kind && r.channel === channel,
  );

  let stateLabel: string;
  let stateClass: string;
  if (hasCampaignRow) {
    stateLabel = t("campaigns.notifications.stateCampaignOverride");
    stateClass = "text-nb-mint";
  } else if (hasOrgRow) {
    stateLabel = t("campaigns.notifications.stateInheritsOrg");
    stateClass = "text-nb-text-2";
  } else {
    stateLabel = t("campaigns.notifications.stateUsingDefault");
    stateClass = "text-nb-text-3";
  }

  return (
    <section className="card space-y-3">
      <header>
        <h2 className="text-sm font-semibold uppercase tracking-wide text-nb-text-2">
          {t("campaigns.notifications.cardTitle")}
        </h2>
        <p className="text-xs text-nb-text-3">
          <Trans
            i18nKey="campaigns.notifications.cardHint"
            values={{ varExample: "{join_url}" }}
            components={[<code key="code" />]}
          />
        </p>
      </header>

      <div className="flex flex-wrap gap-3">
        <label className="text-sm">
          <span className="block text-nb-text-2">
            {t("admin.notifications.kindLabel")}
          </span>
          <select
            className="input"
            value={kind}
            onChange={(e) => setKind(e.target.value as Kind)}
            aria-label={t("admin.notifications.kindAriaLabel")}
          >
            {(Object.keys(KIND_KEYS) as Kind[]).map((k) => (
              <option key={k} value={k}>
                {t(KIND_KEYS[k])}
              </option>
            ))}
          </select>
        </label>
        <label className="text-sm">
          <span className="block text-nb-text-2">
            {t("admin.notifications.channelLabel")}
          </span>
          <select
            className="input"
            value={channel}
            onChange={(e) => setChannel(e.target.value as Channel)}
            aria-label={t("admin.notifications.channelAriaLabel")}
          >
            {(Object.keys(CHANNEL_KEYS) as Channel[]).map((c) => (
              <option key={c} value={c}>
                {t(CHANNEL_KEYS[c])}
              </option>
            ))}
          </select>
        </label>
        <span className={`self-end text-xs ${stateClass}`}>{stateLabel}</span>
      </div>

      {channel === "email" && (
        <label className="block space-y-1 text-sm">
          <span className="text-nb-text-2">
            {t("admin.notifications.subject")}
          </span>
          <input
            className="input w-full"
            value={subject}
            onChange={(e) => {
              setSubject(e.target.value);
              setTouched(true);
            }}
            aria-label={t("admin.notifications.subjectAriaLabel")}
          />
        </label>
      )}

      <label className="block space-y-1 text-sm">
        <span className="text-nb-text-2">
          {channel === "email"
            ? t("admin.notifications.body")
            : t("admin.notifications.message")}
        </span>
        <textarea
          className="input w-full font-mono text-xs"
          rows={channel === "sms" ? 3 : 10}
          value={bodyText}
          onChange={(e) => {
            setBodyText(e.target.value);
            setTouched(true);
          }}
          aria-label={t("admin.notifications.bodyAriaLabel")}
        />
      </label>

      <details className="text-xs">
        <summary className="cursor-pointer text-nb-text-2 hover:text-nb-text">
          {t("admin.notifications.availableVariables", {
            count: Object.keys(variables).length,
          })}
        </summary>
        <ul className="mt-2 space-y-1">
          {Object.entries(variables).map(([name, desc]) => (
            <li key={name}>
              <code className="text-nb-mint">{`{${name}}`}</code>
              <span className="ml-2 text-nb-text-3">{desc}</span>
            </li>
          ))}
        </ul>
      </details>

      <TemplatePreviewAndTest
        kind={kind}
        channel={channel}
        subject={subject}
        bodyText={bodyText}
        campaignId={campaignId}
      />

      {save.isError && (
        <p className="text-xs text-cvp-danger">
          {(save.error as Error).message ??
            t("admin.notifications.saveErrorGeneric")}
        </p>
      )}

      <div className="flex justify-end gap-2">
        {hasCampaignRow && (
          <button
            type="button"
            className="btn-ghost text-sm text-cvp-danger hover:bg-cvp-danger/10"
            onClick={() => {
              if (confirm(t("campaigns.notifications.resetConfirm"))) {
                reset.mutate();
              }
            }}
            disabled={reset.isPending}
          >
            {t("campaigns.notifications.resetButton")}
          </button>
        )}
        <button
          type="button"
          className="btn-primary text-sm"
          onClick={() => save.mutate()}
          disabled={!touched || save.isPending}
        >
          {save.isPending
            ? t("admin.notifications.saving")
            : hasCampaignRow
              ? t("admin.notifications.saveButton")
              : t("campaigns.notifications.createOverrideButton")}
        </button>
      </div>
    </section>
  );
}
